import { ReactNode } from 'react';
import { isServer, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: 0,
      },
    },
  });
}

/**
 * Logging out is an in-app navigation, so the query cache outlives the session. Without this, the
 * next sign-in (a different user, or the same one after their roles changed) mounts the auth
 * Guard on the previous session's cached `getAccount` / `getMyPermissions` results, and — inside
 * the 60s stale window — never refetches them, so the sidebar shows only that old permission set
 * until a full reload empties the cache. Clearing on sign-in makes the Guard fetch its own.
 */
export function clearCacheOnSignIn(queryClient: QueryClient) {
  return useAuthStore.subscribe((state, previous) => {
    if (state.isAuthenticated && !previous.isAuthenticated) queryClient.clear();
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
      clearCacheOnSignIn(browserQueryClient);
    }
    return browserQueryClient;
  }
}

export default function QueryProviders({ children }: Readonly<{ children: ReactNode }>) {
  const queryClient = getQueryClient();

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
